import type { ImpersonationSession } from '../dataProvider/types';

/**
 * Session-state helpers for the view-only impersonation surface (D-impersonation, the
 * Phase 6 surface-4 close). ALL derived off the injected clock, the established
 * suspensions-elapsed / tracking-overdue pattern: a session ends either explicitly (an
 * endedAt write) or by EXPIRY (now past expiresAt, derived, never written).
 */

/**
 * The fixed impersonation-session window, in milliseconds. A session opened at `now`
 * expires at `now + IMPERSONATION_SESSION_DURATION`, after which it is ended-by-expiry
 * (derived). [REVIEW] the length with the support and security teams; 15 minutes is a
 * deliberately short exposure window for a view-only lens over a member's data.
 */
export const IMPERSONATION_SESSION_DURATION = 15 * 60 * 1000;

/** The ISO instant a session opened at `now` (ms) expires. */
export const expiresAfter = (now: number): string =>
  new Date(now + IMPERSONATION_SESSION_DURATION).toISOString();

/** The three states a session can be in, derived off the clock. */
export type SessionPhase = 'active' | 'ended' | 'expired';

/**
 * ACTIVE = not explicitly ended AND the clock has not passed expiresAt. An explicitly
 * ended session is `ended`; an unended one past its expiry is `expired`. Both are inactive.
 */
export const sessionPhase = (
  session: Pick<ImpersonationSession, 'expiresAt' | 'endedAt'>,
  now: number,
): SessionPhase => {
  if (session.endedAt != null) return 'ended';
  if (now >= new Date(session.expiresAt).getTime()) return 'expired';
  return 'active';
};

/** A session is active when it is neither explicitly ended nor past its expiry. */
export const isSessionActive = (
  session: Pick<ImpersonationSession, 'expiresAt' | 'endedAt'>,
  now: number,
): boolean => sessionPhase(session, now) === 'active';
